/**
 * Validation, in the tool rather than behind a link to Google.
 *
 * Two separate verdicts, kept apart on purpose:
 *
 *   Google eligibility — a hard pass or fail against the properties Google
 *   states it requires. Nothing subjective in it.
 *
 *   AI answer readiness — a 0 to 100 score over the properties answer engines
 *   lean on when deciding whether they have identified an entity confidently
 *   enough to cite it. Only checks that apply to the selected types are counted,
 *   so building a single breadcrumb list is not scored against fields that were
 *   never relevant to it.
 *
 * Plus the hygiene rules. These exist because the recurring way generated schema
 * fails in production is not an exotic modelling error, it is a placeholder that
 * shipped, a date in the wrong format, or an image URL nothing outside the
 * office can load.
 */

import { getTypeSpec, type FieldSpec, type SchemaTypeSpec } from "./types";
import type { EntityValues } from "./serialize";

export type IssueSeverity = "error" | "warning" | "info";

/**
 * Every rule this file can raise.
 *
 * Deliberately a closed union rather than a string. The public rule reference on
 * the validator page is keyed by this type, so adding a check here without
 * documenting it fails the build instead of quietly shipping an incomplete
 * reference. See lib/schema-builder/rule-reference.ts.
 */
export type ValidationRule =
  // Values that were never replaced
  | "placeholder"
  | "empty-string"
  // Addresses and images
  | "relative-url"
  | "invalid-url"
  | "invalid-url-scheme"
  | "data-uri"
  | "private-url"
  | "insecure-url"
  | "staging-url"
  // Formats Google parses strictly
  | "date-format"
  | "duration-format"
  | "phone-format"
  | "email-format"
  | "currency-format"
  | "country-format"
  // Properties a type needs
  | "missing-required"
  | "missing-recommended"
  | "product-no-offer"
  | "rating-not-numeric"
  | "rating-out-of-range"
  | "headline-length"
  | "hours-incomplete"
  | "event-past"
  // Shape of the document
  | "missing-context"
  | "missing-type"
  | "unlinked-graph"
  | "faq-visibility";

export interface ValidationIssue {
  severity: IssueSeverity;
  /** Type the issue belongs to, or "" for a graph-wide issue. */
  typeId: string;
  /** Human label for the field, when the issue is about one. */
  field?: string;
  message: string;
  /** Stable rule id, for grouping in the UI and in the rule reference. */
  rule: ValidationRule;
}

export interface AiCheck {
  id: string;
  label: string;
  passed: boolean;
  weight: number;
  /** What to do about it, when it failed. */
  hint: string;
}

export interface TypeVerdict {
  typeId: string;
  label: string;
  /** False when a Google-required property is missing or malformed. */
  googleEligible: boolean;
  missingRequired: string[];
}

export interface ValidationResult {
  issues: ValidationIssue[];
  verdicts: TypeVerdict[];
  aiReadiness: {
    /** 0-100 over applicable checks only. */
    score: number;
    checks: AiCheck[];
  };
  counts: { errors: number; warnings: number; infos: number };
}

// ---------------------------------------------------------------------------
// Hygiene patterns
// ---------------------------------------------------------------------------

/**
 * Strings that mean somebody forgot to replace the example. Deliberately narrow:
 * a false positive here undermines trust in every other check on screen.
 */
const PLACEHOLDER_PATTERNS: Array<{ re: RegExp; label: string }> = [
  { re: /\bexample\.(com|org|net)\b/i, label: "example.com" },
  { re: /\byour(domain|site|company|business|brand)\b/i, label: "a \"your…\" placeholder" },
  { re: /\byour (business|company|site|brand|name) (name|here)\b/i, label: "a \"your…\" placeholder" },
  { re: /lorem ipsum/i, label: "lorem ipsum filler" },
  { re: /\bjohn (doe|smith)\b/i, label: "a placeholder name" },
  { re: /\bjane doe\b/i, label: "a placeholder name" },
  { re: /\b123[-\s.]?456[-\s.]?7890\b/, label: "a placeholder phone number" },
  // 555-0100 to 555-0199 is the range reserved for fiction, which is exactly why
  // we use it in the greyed-out examples and exactly why it must never ship as a
  // real value. The message below says so, since seeing the example flagged as an
  // error is otherwise a fair thing to be confused by.
  {
    re: /\b555[-\s.]?01\d{2}\b/,
    label: "a 555-01xx number, the range reserved for examples so it can never be a real line",
  },
  { re: /\b(todo|tbd|fixme)\b/i, label: "a TODO marker" },
  { re: /\bplaceholder\b/i, label: "the word \"placeholder\"" },
  { re: /\bxxx+\b/i, label: "an XXX placeholder" },
  { re: /\b(insert|enter) (your|the) /i, label: "an instruction left in the value" },
];

/** The one placeholder that belongs in output: the sitelinks search token. */
const LEGITIMATE_PLACEHOLDER = /\{search_term_string\}/;

const PRIVATE_HOST =
  /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|.*\.local|.*\.test|.*\.localhost)$/i;
const STAGING_HINT = /\b(staging|stage|dev|preprod|preview|wpengine\.com|\.vercel\.app)\b/i;

const ISO_DATE_OR_DATETIME =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const ISO_DURATION = /^P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d)(\d+H)?(\d+M)?(\d+(\.\d+)?S)?)?$/;
const CURRENCY = /^[A-Z]{3}$/;
const COUNTRY = /^[A-Za-z]{2}$/;

// ---------------------------------------------------------------------------
// Value access
// ---------------------------------------------------------------------------

function readValue(values: EntityValues, key: string): unknown {
  return values[key];
}

function isFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some((v) => isFilled(v));
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([k]) => !k.startsWith("@"))
      .some(([, v]) => isFilled(v));
  }
  return true;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Walk a field spec against its values, calling `visit` for every leaf that has
 * a value. Handles groups and list rows so nested fields are checked too.
 */
function visitLeaves(
  fields: FieldSpec[],
  values: EntityValues,
  visit: (spec: FieldSpec, value: unknown, labelPath: string) => void,
  labelPrefix = ""
): void {
  for (const spec of fields) {
    const raw = values?.[spec.key];
    const label = labelPrefix ? `${labelPrefix} → ${spec.label}` : spec.label;

    if (spec.kind === "group") {
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        visitLeaves(spec.fields ?? [], raw as EntityValues, visit, label);
      }
      continue;
    }

    if (spec.kind === "list") {
      if (Array.isArray(raw)) {
        raw.forEach((row, i) => {
          if (row && typeof row === "object") {
            visitLeaves(
              spec.fields ?? [],
              row as EntityValues,
              visit,
              `${label} ${i + 1}`
            );
          }
        });
      }
      continue;
    }

    if (isFilled(raw)) visit(spec, raw, label);
  }
}

// ---------------------------------------------------------------------------
// Per-value checks
// ---------------------------------------------------------------------------

function checkPlaceholders(
  text: string,
  typeId: string,
  label: string,
  issues: ValidationIssue[]
): void {
  if (LEGITIMATE_PLACEHOLDER.test(text)) return;
  for (const { re, label: what } of PLACEHOLDER_PATTERNS) {
    if (re.test(text)) {
      issues.push({
        severity: "error",
        typeId,
        field: label,
        rule: "placeholder",
        message: `This still contains ${what}. Replace it with the real value before you publish, or a machine will index the example instead of your business.`,
      });
      return;
    }
  }
}

function checkUrl(
  raw: string,
  typeId: string,
  label: string,
  issues: ValidationIssue[],
  { isImage = false }: { isImage?: boolean } = {}
): void {
  if (raw.startsWith("/") || !/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
    issues.push({
      severity: "error",
      typeId,
      field: label,
      rule: "relative-url",
      message:
        "Use the full address including https://. A relative path means nothing to a crawler reading your markup on its own.",
    });
    return;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    issues.push({
      severity: "error",
      typeId,
      field: label,
      rule: "invalid-url",
      message: "This is not a valid URL.",
    });
    return;
  }

  // Checked before the general scheme guard so an inline image gets the reason
  // that actually explains the problem.
  if (parsed.protocol === "data:") {
    issues.push({
      severity: "error",
      typeId,
      field: label,
      rule: "data-uri",
      message: isImage
        ? "An inline data URI cannot be crawled. Upload the image somewhere public and link to it."
        : "An inline data URI is not a usable address here.",
    });
    return;
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    issues.push({
      severity: "error",
      typeId,
      field: label,
      rule: "invalid-url-scheme",
      message: `${parsed.protocol} is not a web address. Use https://.`,
    });
    return;
  }

  if (PRIVATE_HOST.test(parsed.hostname)) {
    issues.push({
      severity: "error",
      typeId,
      field: label,
      rule: "private-url",
      message:
        "This address only resolves on your own machine, so nothing outside it can load the file. This is the single most common reason generated markup fails once it is live.",
    });
    return;
  }

  if (parsed.protocol === "http:") {
    issues.push({
      severity: "warning",
      typeId,
      field: label,
      rule: "insecure-url",
      message: "Points at http. Use https so the resource is not blocked.",
    });
  }

  if (STAGING_HINT.test(parsed.hostname)) {
    issues.push({
      severity: "warning",
      typeId,
      field: label,
      rule: "staging-url",
      message:
        "This looks like a staging or preview domain. Swap it for the live address before publishing.",
    });
  }
}

function checkLeaf(
  spec: FieldSpec,
  value: unknown,
  typeId: string,
  label: string,
  issues: ValidationIssue[]
): void {
  const strings: string[] = Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : typeof value === "string"
      ? [value]
      : [];

  for (const s of strings) {
    checkPlaceholders(s, typeId, label, issues);
  }

  switch (spec.kind) {
    case "url":
      for (const s of strings) {
        checkUrl(s.trim(), typeId, label, issues, {
          isImage: /image|logo|photo|thumbnail|screenshot/i.test(spec.key),
        });
      }
      break;

    case "date":
    case "datetime": {
      const s = asString(value);
      if (s && !ISO_DATE_OR_DATETIME.test(s)) {
        issues.push({
          severity: "error",
          typeId,
          field: label,
          rule: "date-format",
          message:
            "Google needs ISO 8601, which is 2026-07-29 or 2026-07-29T14:30. We convert the common formats automatically, but this one we could not read.",
        });
      }
      break;
    }

    case "duration": {
      const s = asString(value);
      if (s && !ISO_DURATION.test(s)) {
        issues.push({
          severity: "error",
          typeId,
          field: label,
          rule: "duration-format",
          message:
            "Durations use ISO 8601. PT30M is thirty minutes, PT1H15M is an hour and a quarter.",
        });
      }
      break;
    }

    case "tel": {
      const s = asString(value);
      if (s && !s.startsWith("+")) {
        issues.push({
          severity: "warning",
          typeId,
          field: label,
          rule: "phone-format",
          message:
            "Include the country code, as in +1-512-555-0142. Without it the number is ambiguous outside your own country.",
        });
      }
      break;
    }

    case "email": {
      const s = asString(value);
      if (s && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s)) {
        issues.push({
          severity: "error",
          typeId,
          field: label,
          rule: "email-format",
          message: "This is not a valid email address.",
        });
      }
      break;
    }

    default: {
      const s = asString(value);
      if (spec.key === "priceCurrency" || spec.key === "currency") {
        if (s && !CURRENCY.test(s)) {
          issues.push({
            severity: "error",
            typeId,
            field: label,
            rule: "currency-format",
            message: "Use the three-letter code in capitals, such as USD, EUR or GBP.",
          });
        }
      }
      if (spec.key === "addressCountry" && s && !COUNTRY.test(s)) {
        issues.push({
          severity: "warning",
          typeId,
          field: label,
          rule: "country-format",
          message: "Use the two-letter country code, such as US or GB.",
        });
      }
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Per-type checks
// ---------------------------------------------------------------------------

function checkRequired(
  spec: SchemaTypeSpec,
  values: EntityValues,
  issues: ValidationIssue[]
): string[] {
  const missing: string[] = [];

  for (const field of spec.fields) {
    if (field.key.startsWith("@")) continue;

    if (field.tier === "required") {
      if (!isFilled(readValue(values, field.key))) {
        missing.push(field.label);
        issues.push({
          severity: "error",
          typeId: spec.id,
          field: field.label,
          rule: "missing-required",
          message: `Google requires this for ${spec.label}. Without it the markup is valid but will not earn the rich result.`,
        });
        continue;
      }

      // A filled group or list still has to have its own required parts.
      if (field.kind === "group" || field.kind === "list") {
        const rows: EntityValues[] =
          field.kind === "list"
            ? (readValue(values, field.key) as EntityValues[]) ?? []
            : [(readValue(values, field.key) as EntityValues) ?? {}];

        rows.forEach((row, i) => {
          for (const sub of field.fields ?? []) {
            if (sub.tier !== "required") continue;
            if (!isFilled(row?.[sub.key])) {
              const where =
                field.kind === "list" ? `${field.label} ${i + 1} → ${sub.label}` : `${field.label} → ${sub.label}`;
              missing.push(where);
              issues.push({
                severity: "error",
                typeId: spec.id,
                field: where,
                rule: "missing-required",
                message: `Required for ${spec.label}.`,
              });
            }
          }
        });
      }
      continue;
    }

    if (field.tier === "recommended" && !isFilled(readValue(values, field.key))) {
      issues.push({
        severity: "warning",
        typeId: spec.id,
        field: field.label,
        rule: "missing-recommended",
        message: `Google names this as recommended for ${spec.label}. Adding it improves how the result is shown.`,
      });
    }
  }

  return missing;
}

function checkTypeSpecific(
  spec: SchemaTypeSpec,
  values: EntityValues,
  issues: ValidationIssue[]
): void {
  // Article headlines are truncated past 110 characters.
  if (spec.id === "Article") {
    const headline = asString(readValue(values, "headline"));
    if (headline.length > 110) {
      issues.push({
        severity: "warning",
        typeId: spec.id,
        field: "Headline",
        rule: "headline-length",
        message: `This headline is ${headline.length} characters. Google truncates past 110.`,
      });
    }
  }

  // Ratings have to sit inside their own scale, and be numbers.
  const rating = readValue(values, "aggregateRating") as EntityValues | undefined;
  if (rating && isFilled(rating)) {
    const value = Number(asString(rating.ratingValue));
    const best = Number(asString(rating.bestRating)) || 5;
    const worst = Number(asString(rating.worstRating)) || 1;
    if (asString(rating.ratingValue) && !Number.isFinite(value)) {
      issues.push({
        severity: "error",
        typeId: spec.id,
        field: "Average rating → Rating",
        rule: "rating-not-numeric",
        message: "The rating has to be a number, such as 4.8.",
      });
    } else if (Number.isFinite(value) && (value > best || value < worst)) {
      issues.push({
        severity: "error",
        typeId: spec.id,
        field: "Average rating → Rating",
        rule: "rating-out-of-range",
        message: `A rating of ${value} sits outside the ${worst} to ${best} scale you gave.`,
      });
    }
  }

  // Product needs an offer, a review or a rating to earn anything.
  if (spec.id === "Product") {
    const hasOffer = isFilled(readValue(values, "offers"));
    const hasRating = isFilled(readValue(values, "aggregateRating"));
    if (!hasOffer && !hasRating) {
      issues.push({
        severity: "error",
        typeId: spec.id,
        rule: "product-no-offer",
        message:
          "A product needs a price or a rating to qualify for a product rich result. With neither, this is just a name.",
      });
    }
  }

  // FAQ content has to be on the page. This is a policy rule, not a format one,
  // and it is the way FAQ markup most often gets a site in trouble.
  if (spec.id === "FAQPage") {
    const questions = (readValue(values, "mainEntity") as EntityValues[]) ?? [];
    if (questions.some((q) => isFilled(q?.name))) {
      issues.push({
        severity: "info",
        typeId: spec.id,
        rule: "faq-visibility",
        message:
          "Every question and answer here must also be visible to a visitor on the page. Marking up answers that are not on the page breaks Google's guidelines.",
      });
    }
  }

  // Opening hours without a close time read as open forever.
  if (spec.id === "LocalBusiness") {
    const hours = (readValue(values, "openingHoursSpecification") as EntityValues[]) ?? [];
    hours.forEach((row, i) => {
      const opens = asString(row?.opens);
      const closes = asString(row?.closes);
      if ((opens && !closes) || (!opens && closes)) {
        issues.push({
          severity: "warning",
          typeId: spec.id,
          field: `Opening hours ${i + 1}`,
          rule: "hours-incomplete",
          message: "Give both an opening and a closing time, or leave the row empty.",
        });
      }
    });
  }

  // Events in the past are worse than no event markup.
  if (spec.id === "Event") {
    const start = asString(readValue(values, "startDate"));
    if (start && ISO_DATE_OR_DATETIME.test(start)) {
      const when = new Date(start);
      if (!Number.isNaN(when.getTime()) && when.getTime() < Date.now()) {
        issues.push({
          severity: "warning",
          typeId: spec.id,
          field: "Starts",
          rule: "event-past",
          message: "This start date has already passed, so the event will not be shown.",
        });
      }
    }
  }
}

// ---------------------------------------------------------------------------
// AI answer readiness
// ---------------------------------------------------------------------------

const BUSINESS_TYPES = new Set(["LocalBusiness", "Organization", "Service"]);

function scoreAiReadiness(
  selected: Array<{ typeId: string; values: EntityValues }>,
  origin: string,
  nodeCount: number
): { score: number; checks: AiCheck[] } {
  const ids = new Set(selected.map((s) => s.typeId));
  const byId = new Map(selected.map((s) => [s.typeId, s.values]));
  const checks: AiCheck[] = [];

  const hasBusiness = [...ids].some((id) => BUSINESS_TYPES.has(id));

  /**
   * The selected entities that actually carry a property, read off the specs
   * rather than guessed from the type name.
   *
   * The three checks below used to name Article and Recipe directly, and to
   * treat any content type as proof those properties were reachable. FAQPage is
   * a content type with no description, no author and no date of its own, so
   * picking it added forty points of checks that nothing in the form could
   * satisfy: you could fill in every field on offer and still be told you were
   * missing a description you had in fact written on the Organization. Asking
   * the spec closes that off, and keeps the promise this scorer already makes,
   * that only checks relevant to what you picked are counted.
   */
  const holdersOf = (key: string): EntityValues[] =>
    selected
      .filter(({ typeId }) => getTypeSpec(typeId)?.fields.some((f) => f.key === key))
      .map(({ values }) => values);

  // Named nodes. Without an origin we cannot mint ids at all.
  checks.push({
    id: "identified",
    label: "Every entity has a stable @id",
    weight: 20,
    passed: Boolean(origin),
    hint: "Add your website URL. That is what lets us give each entity a permanent identifier instead of an anonymous blob.",
  });

  // Connected graph.
  checks.push({
    id: "connected",
    label: "Entities are linked to each other",
    weight: 20,
    passed: nodeCount >= 2 && Boolean(origin),
    hint: "Add the Organization and Website types alongside this one. A page entity that states who published it is far easier to trust than one that stands alone.",
  });

  // sameAs on the identity node is the strongest disambiguation signal there is.
  if (hasBusiness || ids.has("Person")) {
    const identity =
      byId.get("Organization") ?? byId.get("LocalBusiness") ?? byId.get("Person") ?? {};
    checks.push({
      id: "sameAs",
      label: "Linked to external profiles with sameAs",
      weight: 20,
      passed: isFilled(identity.sameAs),
      hint: "Add your LinkedIn, Wikipedia, Wikidata or main social profiles. This is how an answer engine confirms it has the right company and not a similarly named one.",
    });
  }

  // A real description is usually the text that gets quoted back. Any selected
  // entity can supply it, so the longest one on the page answers for all.
  const describable = holdersOf("description").concat(holdersOf("headline"));
  if (describable.length) {
    const longest = describable.reduce(
      (best, v) =>
        Math.max(best, asString(v.description).length, asString(v.headline).length),
      0
    );
    checks.push({
      id: "description",
      label: "Has a substantial plain-language description",
      weight: 15,
      passed: longest >= 50,
      hint: "Write two or three complete sentences describing this. Short fragments give an answer engine nothing to quote.",
    });
  }

  const authored = holdersOf("author");
  if (authored.length) {
    checks.push({
      id: "author",
      label: "Names an author with a link to their bio",
      weight: 15,
      passed: authored.some((v) => {
        const author = v.author as EntityValues | undefined;
        return Boolean(author && isFilled(author.name) && isFilled(author.url));
      }),
      hint: "Add the author name and a link to their bio page. Attributable content is cited noticeably more often than anonymous content.",
    });
  }

  const dateable = holdersOf("datePublished");
  if (dateable.length) {
    checks.push({
      id: "dates",
      label: "States when it was published",
      weight: 10,
      passed: dateable.some((v) => isFilled(v.datePublished)),
      hint: "Add a publish date, and a modified date if you keep the page updated. Answer engines discount content they cannot date.",
    });
  }

  if (hasBusiness) {
    const business = byId.get("LocalBusiness") ?? byId.get("Organization") ?? {};
    const address = business.address as EntityValues | undefined;
    checks.push({
      id: "location",
      label: "States a specific location",
      weight: 15,
      passed: Boolean(address && isFilled(address.addressLocality) && isFilled(address.addressRegion)),
      hint: "Fill in at least the city and the state or region. Location is how you get surfaced for questions with a place in them.",
    });
    checks.push({
      id: "contact",
      label: "Gives a way to make contact",
      weight: 10,
      passed: isFilled(business.telephone) || isFilled(business.email),
      hint: "Add a phone number or an email address.",
    });
  }

  const applicable = checks.reduce((sum, c) => sum + c.weight, 0);
  const earned = checks.filter((c) => c.passed).reduce((sum, c) => sum + c.weight, 0);
  const score = applicable ? Math.round((earned / applicable) * 100) : 0;

  return { score, checks };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function validateSelection(
  selected: Array<{ typeId: string; values: EntityValues }>,
  context: { origin: string; nodeCount: number }
): ValidationResult {
  const issues: ValidationIssue[] = [];
  const verdicts: TypeVerdict[] = [];

  for (const { typeId, values } of selected) {
    const spec = getTypeSpec(typeId);
    if (!spec) continue;

    const missingRequired = checkRequired(spec, values, issues);
    checkTypeSpecific(spec, values, issues);
    visitLeaves(spec.fields, values, (fieldSpec, value, label) =>
      checkLeaf(fieldSpec, value, typeId, label, issues)
    );

    const hasFormatError = issues.some(
      (i) => i.typeId === typeId && i.severity === "error" && i.rule !== "missing-required"
    );

    verdicts.push({
      typeId,
      label: spec.label,
      googleEligible:
        spec.richResultStatus !== "none" && missingRequired.length === 0 && !hasFormatError,
      missingRequired,
    });
  }

  const aiReadiness = scoreAiReadiness(selected, context.origin, context.nodeCount);

  return {
    issues,
    verdicts,
    aiReadiness,
    counts: {
      errors: issues.filter((i) => i.severity === "error").length,
      warnings: issues.filter((i) => i.severity === "warning").length,
      infos: issues.filter((i) => i.severity === "info").length,
    },
  };
}

/**
 * Validate JSON-LD a visitor pasted in, which is the validator-mode path.
 * Structural only: we cannot know what their page says, so we check shape,
 * required properties per type, and the same hygiene rules.
 */
export interface PastedValidation {
  ok: boolean;
  parseError?: string;
  /** Types we found, in graph order. */
  found: Array<{ type: string; hasId: boolean }>;
  issues: ValidationIssue[];
  /** True when nodes reference each other by @id. */
  linked: boolean;
}

export function validatePasted(raw: string): PastedValidation {
  const issues: ValidationIssue[] = [];
  const text = raw.trim();

  if (!text) {
    return { ok: false, parseError: "Paste some JSON-LD to check.", found: [], issues, linked: false };
  }

  // Accept a full <script> block, since that is what people copy off a page.
  const unwrapped = text
    .replace(/^<script[^>]*>/i, "")
    .replace(/<\/script>\s*$/i, "")
    .trim();

  let parsed: unknown;
  try {
    parsed = JSON.parse(unwrapped);
  } catch (err) {
    return {
      ok: false,
      parseError:
        err instanceof Error
          ? `This is not valid JSON: ${err.message}`
          : "This is not valid JSON.",
      found: [],
      issues,
      linked: false,
    };
  }

  const roots = Array.isArray(parsed) ? parsed : [parsed];
  const nodes: Array<Record<string, unknown>> = [];
  for (const root of roots) {
    if (!root || typeof root !== "object") continue;
    const record = root as Record<string, unknown>;
    if (Array.isArray(record["@graph"])) {
      for (const n of record["@graph"] as unknown[]) {
        if (n && typeof n === "object") nodes.push(n as Record<string, unknown>);
      }
    } else {
      nodes.push(record);
    }
  }

  if (!nodes.length) {
    return {
      ok: false,
      parseError: "Valid JSON, but no schema nodes in it.",
      found: [],
      issues,
      linked: false,
    };
  }

  const hasContext = roots.some(
    (r) => r && typeof r === "object" && "@context" in (r as Record<string, unknown>)
  );
  if (!hasContext) {
    issues.push({
      severity: "error",
      typeId: "",
      rule: "missing-context",
      message:
        'No @context. Add "@context": "https://schema.org" at the top level or nothing will read this as schema.',
    });
  }

  const found: Array<{ type: string; hasId: boolean }> = [];
  const declaredIds = new Set<string>();
  let referencesIds = false;

  const scan = (value: unknown, path: string, typeLabel: string): void => {
    if (Array.isArray(value)) {
      value.forEach((v, i) => scan(v, `${path}[${i}]`, typeLabel));
      return;
    }
    if (!value || typeof value !== "object") return;

    const record = value as Record<string, unknown>;
    if (typeof record["@id"] === "string") {
      if (Object.keys(record).length === 1) referencesIds = true;
      else declaredIds.add(record["@id"]);
    }

    for (const [key, raw] of Object.entries(record)) {
      if (key === "@type" || key === "@context") continue;
      const where = path ? `${path}.${key}` : key;

      if (typeof raw === "string") {
        checkPlaceholders(raw, typeLabel, where, issues);

        if (/^(url|logo|image|thumbnailUrl|contentUrl|embedUrl|screenshot|sameAs|item)$/.test(key)) {
          checkUrl(raw.trim(), typeLabel, where, issues, {
            isImage: /image|logo|thumbnail|screenshot/i.test(key),
          });
        }
        if (/^(datePublished|dateModified|uploadDate|startDate|endDate|datePosted|validThrough|foundingDate)$/.test(key)) {
          if (!ISO_DATE_OR_DATETIME.test(raw.trim())) {
            issues.push({
              severity: "error",
              typeId: typeLabel,
              field: where,
              rule: "date-format",
              message: `"${raw}" is not ISO 8601. Google needs 2026-07-29 or 2026-07-29T14:30.`,
            });
          }
        }
        if (/^(totalTime|prepTime|cookTime|duration)$/.test(key) && !ISO_DURATION.test(raw.trim())) {
          issues.push({
            severity: "error",
            typeId: typeLabel,
            field: where,
            rule: "duration-format",
            message: `"${raw}" is not an ISO 8601 duration. Use PT30M for thirty minutes.`,
          });
        }
        if (raw.trim() === "") {
          issues.push({
            severity: "warning",
            typeId: typeLabel,
            field: where,
            rule: "empty-string",
            message:
              "Empty value. Remove the property rather than sending an empty string, which validators flag.",
          });
        }
      } else {
        scan(raw, where, typeLabel);
      }
    }
  };

  for (const node of nodes) {
    const rawType = node["@type"];
    const typeName = Array.isArray(rawType)
      ? rawType.map(String).join(", ")
      : typeof rawType === "string"
        ? rawType
        : "";

    if (!typeName) {
      issues.push({
        severity: "error",
        typeId: "",
        rule: "missing-type",
        message: "A node has no @type, so nothing can tell what it describes.",
      });
    }

    found.push({ type: typeName || "(no @type)", hasId: typeof node["@id"] === "string" });
    scan(node, "", typeName);

    // Required properties, where we know the type.
    const spec = SIMPLE_REQUIRED[typeName];
    if (spec) {
      for (const key of spec) {
        if (!isFilled(node[key])) {
          issues.push({
            severity: "error",
            typeId: typeName,
            field: key,
            rule: "missing-required",
            message: `${typeName} requires ${key} to be eligible for its rich result.`,
          });
        }
      }
    }
  }

  if (nodes.length > 1 && !referencesIds) {
    issues.push({
      severity: "warning",
      typeId: "",
      rule: "unlinked-graph",
      message:
        `You have ${nodes.length} separate nodes and none of them reference each other by @id. ` +
        "A machine has to guess how they relate. Naming each node and pointing the relationships at those names removes the guess.",
    });
  }

  return {
    ok: !issues.some((i) => i.severity === "error"),
    found,
    issues,
    linked: referencesIds && declaredIds.size > 0,
  };
}

/** Google-required properties, keyed by @type, for the paste path. */
const SIMPLE_REQUIRED: Record<string, string[]> = {
  Article: ["headline"],
  BlogPosting: ["headline"],
  NewsArticle: ["headline"],
  Product: ["name"],
  Recipe: ["name", "image", "recipeIngredient", "recipeInstructions"],
  Event: ["name", "startDate", "location"],
  JobPosting: ["title", "description", "datePosted", "hiringOrganization", "jobLocation"],
  VideoObject: ["name", "description", "thumbnailUrl", "uploadDate"],
  FAQPage: ["mainEntity"],
  HowTo: ["name", "step"],
  Course: ["name", "description", "provider"],
  LocalBusiness: ["name", "address"],
  Organization: ["name", "url"],
  BreadcrumbList: ["itemListElement"],
  SoftwareApplication: ["name", "applicationCategory"],
  Review: ["itemReviewed", "reviewRating", "author"],
  Person: ["name"],
  WebSite: ["name", "url"],
};
